import React, { useEffect, useRef, useState } from 'react';
import { FlightButton, getIcon } from '@flybits/design-system';
import { ReactComponent as FlowBuilderIcon } from 'assets/icons/flow-builder.svg';
import { ReactComponent as TouchpointEditorIcon } from 'assets/icons/touchpoint-editor.svg';

type TOverlayProps = {
  className: string;
  isFlowOnly?: boolean;
  isTouchpointEditorEnabled?: boolean;
  isFlowV2Enabled?: boolean;
  onSelectTouchpoint: () => void;
  onSelectFlow: () => void;
  onSelectFlowV2?: () => void;
};

const TemplateCardOverlay: React.FC<TOverlayProps> = ({
  className,
  onSelectTouchpoint,
  onSelectFlow,
  onSelectFlowV2,
  isFlowOnly = false,
  isTouchpointEditorEnabled = false,
  isFlowV2Enabled = false,
}) => {
  const showOverflowMenu = !isFlowOnly && (isTouchpointEditorEnabled || isFlowV2Enabled);
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  // Ref for the dropdown
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click or tab outside
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    const handleKeyDown = () => {
      if (dropdownRef.current && !dropdownRef.current.contains(document.activeElement)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownOpen]);

  return (
    <div className={`${className}__overlay`}>
      <FlightButton label="Use template" className={`${className}__overlay__button`} onClick={onSelectFlow} />
      {showOverflowMenu && (
        <>
          <button
            className={`${className}__overflow`}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={() => setDropdownOpen((prev) => !prev)}
          >
            {getIcon('moreHori', {})}
          </button>
          {isDropdownOpen && (
            <div className={`${className}__overflow__dropdown`} ref={dropdownRef} tabIndex={-1}>
              {isTouchpointEditorEnabled && (
                <button onClick={onSelectTouchpoint} onKeyDown={(e) => e.stopPropagation()}>
                  <TouchpointEditorIcon />
                  Use Touchpoint Editor
                </button>
              )}
              {isTouchpointEditorEnabled && (
                <button onClick={onSelectFlow} onKeyDown={(e) => e.stopPropagation()}>
                  <FlowBuilderIcon />
                  Use Flow Visualizer
                </button>
              )}
              {isFlowV2Enabled && (
                <button onClick={onSelectFlowV2} onKeyDown={(e) => e.stopPropagation()}>
                  <FlowBuilderIcon />
                  Use New Flow Visualizer
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TemplateCardOverlay;
